import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { motion, useReducedMotion } from "framer-motion";
import api from "../../utils/api";
import Icon from "../common/icons";
import { fetchUpdates, updatesChanged } from "./updatesStore";
import "./accountLinked.css";

/**
 * "Your account is now linked to STEM 11-A." (C6). The guidance office's class list linked the
 * student's walk-in account to their class record (student_import._link_walk_in), which keeps one
 * StudentUpdate row of kind "account_linked". The student hears it once:
 *  - AccountLinkedNote: a green card at the top of My profile until they tap "Got it" (marks it read)
 *  - announceAccountLinked(): a toast at the next sign-in or page load, once per update
 * The row also stays in the Updates list under the bell.
 */

const KIND = "account_linked";
const SEEN_KEY = (id) => `anna.accountLinkedToast.${id}`;
const quiet = { skipErrorToast: true };

const unreadLinked = async () => {
  const data = await fetchUpdates();
  return (data.updates || []).find((row) => row.kind === KIND && !row.read) || null;
};

const markRead = (id) =>
  api.post("/api/student-updates/read/", { ids: [id] }, quiet)
    .then((r) => updatesChanged({ unread: Number(r.data?.unread) || 0 }))
    .catch(() => {});

const looked = new Set();

/** The toast at the next sign-in. Safe to call often: one request per student per page load, one toast per update. */
export const announceAccountLinked = async (who = "student") => {
  if (looked.has(who)) return;
  // My profile shows the same words in its green card, so no toast there
  if (window.location.pathname.replace(/\/+$/, "") === "/account") return;
  looked.add(who);
  try {
    const row = await unreadLinked();
    if (!row) return;
    try {
      if (localStorage.getItem(SEEN_KEY(row.id))) return;
      localStorage.setItem(SEEN_KEY(row.id), "1");
    } catch (error) {
      // No storage (private window): the toast may show again next time, which is fine
    }
    toast.success(`${row.title} ${row.body}`.trim(), { autoClose: 9000 });
  } catch (error) {
    // Updates are a nice-to-have here; never get in the way of signing in
  }
};

export default function AccountLinkedNote() {
  const reduce = useReducedMotion();
  const [row, setRow] = useState(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    unreadLinked().then((found) => { if (alive.current) setRow(found); }).catch(() => {});
    return () => { alive.current = false; };
  }, []);

  if (!row) return null;
  const done = () => {
    markRead(row.id);
    setRow(null);
  };

  return (
    <motion.section
      className="al-linked"
      role="status"
      initial={reduce ? false : { opacity: 0, y: -10, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
    >
      <span className="al-linked-tile" aria-hidden="true"><Icon name="check-circle" size={26} /></span>
      <p className="al-linked-text">
        <b>{row.title}</b> {row.body}
      </p>
      <button type="button" className="sb-btn is-small al-linked-ok" onClick={done}>Got it</button>
    </motion.section>
  );
}
